import type {PermissionType} from '@hconnect/apiclient'
import {useUpmPlantContext} from '@hconnect/upm'
import {useMemo} from 'react'

import {checkPermissionAccessByDataScope} from '../helpers/checkPermissionAccessByDataScope'
import type {DataScope} from '../types'

import {usePermissionsParsedQuery} from './queries'

/**
 * Convenience hook that checks a single permission type using the current UPM plant context.
 * Prefer this over `usePermission` when the plant context can be read from UpmPlantProvider.
 *
 * @param type - The permission type to check.
 * @param dataScopeTestFn - Optional extra filter on the permission's data scope (e.g. comment category).
 */
export const usePlantPermission = (
  type: PermissionType,
  dataScopeTestFn?: (dataScope: Partial<DataScope & Record<string, unknown>>) => boolean
): boolean => {
  const {data: permissions} = usePermissionsParsedQuery([type])
  const {plantId: plantCode, country: countryId} = useUpmPlantContext()

  return useMemo(() => {
    if (!permissions) {
      return false
    }
    const foundPermissions = permissions
      .filter(({parsedDataScope}) =>
        checkPermissionAccessByDataScope(countryId, plantCode, parsedDataScope)
      )
      .filter(({parsedDataScope}) =>
        dataScopeTestFn
          ? dataScopeTestFn(parsedDataScope as Partial<DataScope & Record<string, unknown>>)
          : true
      )
    return foundPermissions.some(({permissionType}) => permissionType === type)
  }, [permissions, type, plantCode, countryId, dataScopeTestFn])
}
